import type { Agency } from '@saaga/shared/interfaces/agencies.interface'
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useOrgSlug } from '@/hooks/use-org-slug.hook'
import { agencyRoutes, orgRoutes } from '@/routes/org-routes.const'

import { getTabFromPath } from './agency-tab.util'

export interface UseAgenciesNavigationReturn {
  handleCreateAgency: () => void
  handleStartChat: (agency: Agency) => void
  handleSeeDetails: (agency: Agency) => void
  handleEditAgency: (agency: Agency) => void
  handleAgencyClick: (agency: Agency) => void
}

export const useAgenciesNavigation = (): UseAgenciesNavigationReturn => {
  const navigate = useNavigate()
  const location = useLocation()
  const orgSlug = useOrgSlug()

  const currentTab = getTabFromPath(location.pathname)

  const handleCreateAgency = useCallback((): void => {
    navigate(`/${orgSlug}/${orgRoutes.agencies}/${agencyRoutes.studio}/${agencyRoutes.new}`)
  }, [navigate, orgSlug])

  const handleStartChat = useCallback(
    (agency: Agency): void => {
      navigate(`/${orgSlug}/${orgRoutes.chat}/${agency.slug}`)
    },
    [navigate, orgSlug]
  )

  const handleSeeDetails = useCallback(
    (agency: Agency): void => {
      navigate(`/${orgSlug}/${orgRoutes.agencies}/${currentTab}/${agency.slug}`)
    },
    [navigate, orgSlug, currentTab]
  )

  const handleEditAgency = useCallback(
    (agency: Agency): void => {
      navigate(
        `/${orgSlug}/${orgRoutes.agencies}/${agencyRoutes.studio}/${agency.slug}/${agencyRoutes.edit}`
      )
    },
    [navigate, orgSlug]
  )

  const handleAgencyClick = useCallback(
    (agency: Agency): void => {
      navigate(`/${orgSlug}/${orgRoutes.agencies}/${currentTab}/${agency.slug}`)
    },
    [navigate, orgSlug, currentTab]
  )

  return {
    handleCreateAgency,
    handleStartChat,
    handleSeeDetails,
    handleEditAgency,
    handleAgencyClick,
  }
}
